import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  ClockIcon, 
  CheckCircleIcon,
  XCircleIcon,
  PlayCircleIcon,
  PauseCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useServer } from '../contexts/ServerContext';

const BackupLogs = () => {
  const { selectedServer } = useServer();
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    limit: 50
  });

  // Carregar jobs de backup
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        serverId: selectedServer,
        limit: filters.limit
      });
      
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const response = await fetch(`/api/backup-config/jobs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      } else {
        console.error('Erro ao carregar jobs de backup');
      }
    } catch (error) {
      console.error('Erro ao buscar jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar logs do sistema
  const fetchSystemLogs = async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/backup-system/logs?lines=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || 'Nenhum log encontrado');
      } else {
        setLogs('Erro ao carregar logs do sistema');
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      setLogs('Erro ao conectar com o servidor');
    } finally {
      setLogsLoading(false);
    }
  };

  // Buscar detalhes de um job específico
  const fetchJobDetails = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/backup-config/jobs/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.job;
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do job:', error);
    }
    return null;
  };

  useEffect(() => {
    fetchJobs();
    fetchSystemLogs();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(() => {
      fetchJobs();
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedServer, filters]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'running':
        return <PlayCircleIcon className="h-5 w-5 text-blue-500" />;
      case 'pending':
        return <PauseCircleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Concluído';
      case 'failed': return 'Falhou';
      case 'running': return 'Executando';
      case 'pending': return 'Pendente';
      default: return 'Desconhecido';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'running': return 'text-blue-600 bg-blue-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const parseResults = (resultsString) => {
    if (!resultsString) return null;
    try {
      return JSON.parse(resultsString);
    } catch {
      return null;
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
          Logs de Backup
        </h1>
        <p className="text-gray-600 mt-1">Histórico detalhado e logs de execução dos backups</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filtros:</span>
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os status</option>
            <option value="completed">Concluído</option>
            <option value="failed">Falhou</option>
            <option value="running">Executando</option>
            <option value="pending">Pendente</option>
          </select>

          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os tipos</option>
            <option value="manual">Manual</option>
            <option value="scheduled">Agendado</option>
          </select>

          <select
            value={filters.limit}
            onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>25 jobs</option>
            <option value={50}>50 jobs</option>
            <option value={100}>100 jobs</option>
          </select>

          <button
            onClick={fetchJobs}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lista de Jobs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Histórico de Jobs ({jobs.length})
            </h2>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
                <p className="text-gray-500">Carregando jobs...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p>Nenhum job de backup encontrado</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {jobs.map((job) => {
                  const isExpanded = expandedJobs.has(job.id);
                  const results = parseResults(job.results);

                  return (
                    <div key={job.id} className="p-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleJobExpansion(job.id)}
                      >
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(job.status)}
                          <div>
                            <p className="font-medium text-gray-900">
                              Job {job.jobId?.substring(0, 8)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {job.type === 'manual' ? 'Manual' : 'Agendado'} • 
                              {formatDate(job.created_at)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                            {getStatusText(job.status)}
                          </span>
                          {isExpanded ? (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Detalhes expandidos */}
                      {isExpanded && (
                        <div className="mt-4 pl-8 space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-gray-600">Duração:</span>
                              <span className="ml-2">{formatDuration(job.duration)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">Arquivos:</span>
                              <span className="ml-2">{job.totalFiles || 0}</span>
                            </div>
                          </div>

                          {job.startedAt && (
                            <div>
                              <span className="font-medium text-gray-600">Iniciado:</span>
                              <span className="ml-2">{formatDate(job.startedAt)}</span>
                            </div>
                          )}

                          {job.completedAt && (
                            <div>
                              <span className="font-medium text-gray-600">Finalizado:</span>
                              <span className="ml-2">{formatDate(job.completedAt)}</span>
                            </div>
                          )}

                          {results && (
                            <div>
                              <span className="font-medium text-gray-600">Resultados:</span>
                              <div className="ml-2 mt-1 p-2 bg-gray-50 rounded text-xs">
                                <div>Arquivos criados: {results.filesCreated || 0}</div>
                                <div>Erros: {results.errors || 0}</div>
                                {results.error && (
                                  <div className="text-red-600 mt-1">Erro: {results.error}</div>
                                )}
                              </div>
                            </div>
                          )}

                          {job.errorMessage && (
                            <div>
                              <span className="font-medium text-gray-600">Erro:</span>
                              <div className="ml-2 mt-1 p-2 bg-red-50 text-red-700 rounded text-xs">
                                {job.errorMessage}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Logs do Sistema */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Logs do Executor</h2>
            <button
              onClick={fetchSystemLogs}
              className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Atualizar
            </button>
          </div>

          <div className="p-4">
            {logsLoading ? (
              <div className="text-center py-8">
                <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-500 mx-auto mb-2" />
                <p className="text-gray-500">Carregando logs...</p>
              </div>
            ) : (
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{logs}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupLogs;